/*
 * <pdp-reviews> — wires the section's outline "write a review" control
 * to the judge.me widget's own (hidden) trigger, and fixes up the
 * widget's lazy-loaded gallery thumbnails. Scopes everything to the
 * element instance so multiple instances never collide.
 *
 * Progressive enhancement: the widget's native write-review link stays
 * visible until JS confirms the proxy button is wired (signalled by the
 * `pdp-reviews--js` class), so a failed/blocked script never leaves
 * customers without a way to write a review.
 */
if (!customElements.get('pdp-reviews')) {
  customElements.define(
    'pdp-reviews',
    class PdpReviews extends HTMLElement {
      connectedCallback() {
        const root = this;
        const btn = root.querySelector('.pdp-reviews__write');
        if (!btn) return;

        // Persist the wired state on the instance (not a local), so a
        // Theme Editor disconnect/reconnect of the same element never
        // re-attaches the click handler — which would fire link.click()
        // multiple times per user click.
        const wire = () => {
          if (this.wired) return true;
          const link = root.querySelector('.jdgm-write-rev-link');
          if (!link) return false;
          // Reveal the proxy and hide the native link together: the
          // class drives the CSS that hides the widget's own link, so
          // it is only ever hidden once the proxy is actually usable.
          btn.hidden = false;
          root.classList.add('pdp-reviews--js');
          btn.addEventListener('click', () => {
            link.click();
            // First click injects the form but can leave its wrapper
            // closed; nudge it open if nothing became visible.
            setTimeout(() => {
              const wrapper = root.querySelector('.jdgm-form-wrapper');
              if (wrapper && wrapper.offsetHeight === 0) link.click();
            }, 600);
          });
          this.wired = true;
          return true;
        };

        // The widget's lazy loader can leave gallery thumbnails stuck on
        // data-src; perform the same swap it would. Idempotent — the
        // selector only matches thumbnails not yet given a src.
        const loadThumbs = () => {
          root.querySelectorAll('.jdgm-gallery__thumbnail[data-src]:not([src])').forEach((img) => {
            img.addEventListener('load', () => {
              const wrapper = img.closest('.jdgm-gallery__thumbnail-wrapper');
              if (wrapper) wrapper.classList.remove('jdgm--loading');
            });
            img.src = img.dataset.src;
          });
        };

        // Initial pass for anything already present.
        loadThumbs();
        wire();

        // The widget hydrates asynchronously, so keep watching: this
        // covers both the late-injected write-review link and slow-but-
        // successful thumbnail loads (not just the immediate case).
        const target = root.querySelector('.pdp-reviews__widget');
        if (!target) return;

        // Defensive: tear down anything left over from a prior connect
        // (Theme Editor can move/re-insert a section) before re-observing.
        this.teardown();
        this.observer = new MutationObserver(() => {
          loadThumbs();
          wire();
        });
        this.observer.observe(target, { childList: true, subtree: true });
        // Stop watching after 15s so the observer never lingers. If
        // judge.me hydrates slower than this (rare, very slow networks),
        // the proxy button stays hidden — but the widget's native
        // write-review link remains visible as the fallback, so the
        // customer can still write a review.
        this.stopTimer = setTimeout(() => this.teardown(), 15000);
      }

      disconnectedCallback() {
        // Section unloaded (e.g. Theme Editor) — stop observing and drop
        // the pending timeout so nothing keeps running after removal.
        this.teardown();
      }

      teardown() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        if (this.stopTimer) {
          clearTimeout(this.stopTimer);
          this.stopTimer = null;
        }
      }
    }
  );
}
